import { useCallback, useEffect, useMemo, useRef } from 'react'
import { NotificationChange } from '@/types/doc-changes'
import { useDocumentsChangeSetsContext } from '@/components/validation/providers/DocumentsChangeSetsProvider'
import useApplyDocumentChanges, {
  UseApplyDocumentChangesOptions,
} from './useApplyDocumentChanges'
import useSubscribe from './useSubscribe'

type UseDocumentValidationSubscribeOptions = UseApplyDocumentChangesOptions

export default function useDocumentValidationSubscribe({
  documentId,
  ...options
}: UseDocumentValidationSubscribeOptions) {
  const {
    documentsChangeSetIdsRef,
    documentLastChangeSetSentAt,
    setDocumentLastChangeSetSentAt,
    markChangeSetIdsApplied,
    tryConsumeAppliedChangeSetIds,
  } = useDocumentsChangeSetsContext()
  const changeSetTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const { applyNotificationChange, resetData } = useApplyDocumentChanges({
    documentId,
    ...options,
  })

  const lastChangeSetSentAt = useMemo(
    () => (documentId ? documentLastChangeSetSentAt[documentId] : ''),
    [documentId, documentLastChangeSetSentAt],
  )

  useEffect(() => {
    if (!documentId) return

    if (lastChangeSetSentAt) {
      if (changeSetTimeoutRef.current) {
        clearTimeout(changeSetTimeoutRef.current) // Clear any existing timeout
      }

      // If after 30 seconds of the last request, we have not received all the notifications
      // we just reset the data
      changeSetTimeoutRef.current = setTimeout(() => {
        if (documentsChangeSetIdsRef.current[documentId]?.size) {
          documentsChangeSetIdsRef.current[documentId] = new Set()

          setDocumentLastChangeSetSentAt?.((prev) => {
            if (!prev[documentId]) return prev
            const updated = { ...prev }
            delete updated[documentId]
            return updated
          })

          resetData()
        }
      }, 30000)
    }

    return () => {
      if (changeSetTimeoutRef.current) {
        clearTimeout(changeSetTimeoutRef.current)
      }
    }
  }, [
    documentId,
    documentsChangeSetIdsRef,
    lastChangeSetSentAt,
    resetData,
    setDocumentLastChangeSetSentAt,
  ])

  useSubscribe({
    onMessage: useCallback(
      async (event: MessageEvent) => {
        if (!documentId) return

        const data = JSON.parse(event.data)
        if (
          (data.obj_id && data.obj_id !== documentId) ||
          data.action !== '/v2/pd-rules/doc_changes|new' ||
          (data.stripped_partial_msg &&
            data.stripped_partial_msg.obj_id !== documentId)
        )
          return

        const currentNotificationChange = data.stripped_partial_msg
          ? {
              // We only keep the change ids, which should be there unless they are too long, which is not likely.
              // We leave the other data as empty, so it can be marked as requiring invalidation.
              change_set_ids: data.stripped_partial_msg.action_data
                .change_set_ids as string[],
              values: [],
              rows: [],
              tags: [],
              doc_flags: [],
              id_changes: [],
              chips: [],
            }
          : (data.action_data as NotificationChange)

        // The sync response can apply this same notification before or after
        // this message arrives, so whichever is second drops its copy.
        const { change_set_ids: changeSetIds } = currentNotificationChange
        if (tryConsumeAppliedChangeSetIds?.(changeSetIds)) return

        applyNotificationChange(currentNotificationChange)
        markChangeSetIdsApplied?.(changeSetIds)
      },
      [
        documentId,
        tryConsumeAppliedChangeSetIds,
        markChangeSetIdsApplied,
        applyNotificationChange,
      ],
    ),
    body: documentId
      ? [
          {
            endpoint_path: '/v2/pd/documents',
            obj_ids: [documentId],
          },
        ]
      : undefined,
  })
}
